import { z } from 'zod';
import { RoleKey } from '../permissions.js';
/**
 * Request schemas, enums and labels of the contacts module (Kontakte 01–09, Kontaktakte I/II/III)
 * shared by the API (validation) and the SPA (forms, pills). German strings are the verbatim texts of
 * the screens; identifiers are English (docs/glossary.md).
 */
export const dayKeySchema = z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'Bitte ein Datum im Format JJJJ-MM-TT angeben.');
export const timeOfDaySchema = z
    .string()
    .regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Bitte eine Uhrzeit im Format HH:MM angeben.');
export const lifecycleTones = {
    new: 'info',
    contacted: 'accent',
    call_booked: 'accent',
    prospect: 'accent',
    customer: 'success',
    existing_customer: 'success',
    follow_up: 'warning',
    no_show: 'warning',
    not_reached: 'neutral',
    no_interest: 'neutral',
    disqualified: 'neutral',
};
/** The fixed "Quelle" options of the create form; organizations extend them with what they already used. */
export const defaultContactSources = [
    'Empfehlung',
    'Meta',
    'Google',
    'Website',
    'Messe',
    'Tippgeber',
    'Sonstiges',
];
/** "Nächster Schritt" of the create dialog (Kontakte 04): exactly one step per new contact (H2). */
export const contactNextStepKinds = ['dialer_pool', 'book_appointment', 'task_for_me'];
export const contactNextStepLabels = {
    dialer_pool: { title: 'In den Dialer-Pool', subtext: 'Setter rufen an' },
    book_appointment: { title: 'Termin buchen', subtext: 'Buchungslink' },
    task_for_me: { title: 'Aufgabe an mich', subtext: 'mit Datum' },
};
export const contactNextStepSchema = z.discriminatedUnion('kind', [
    z.object({ kind: z.literal('dialer_pool') }),
    z.object({ kind: z.literal('book_appointment') }),
    z.object({
        kind: z.literal('task_for_me'),
        title: z.string().trim().min(1, 'Pflichtangabe.').max(200).optional(),
        dueOn: dayKeySchema,
        dueTime: timeOfDaySchema.optional(),
    }),
]);
export const createContactBodySchema = z.object({
    firstName: z.string().trim().min(1, 'Pflichtangabe.').max(100),
    lastName: z.string().trim().min(1, 'Pflichtangabe.').max(100),
    phone: z.string().trim().max(40).optional(),
    email: z.string().trim().max(200).optional(),
    sourceChannel: z.string().trim().min(1, 'Quelle ist Pflicht für die Auswertung.').max(100),
    referrerId: z.string().uuid().nullable().optional(),
    nextStep: contactNextStepSchema,
    createDespiteDuplicate: z.boolean().optional(),
    duplicateOfId: z.string().uuid().optional(),
});
/** Follow-up step of the lifecycle dialog (Kontakte 06) and of the call editor (III.T3). */
export const followUpSchema = z.object({
    dueOn: dayKeySchema,
    dueTime: timeOfDaySchema.optional(),
    assigneeId: z.string().uuid().optional(),
});
/**
 * Targets of the lifecycle dialog (Kontakte 06), in the order of its radio cards. By hand every stage can
 * be set from every other stage, in any direction (Paul 29.10.2026, D-333).
 */
export const manualLifecycleTransitions = [
    'new',
    'contacted',
    'call_booked',
    'prospect',
    'customer',
    'existing_customer',
    'follow_up',
    'not_reached',
    'no_show',
    'no_interest',
    'disqualified',
];
export const lifecycleChangeBodySchema = z.object({
    transition: z.enum(manualLifecycleTransitions),
    reason: z.string().trim().max(1000).optional(),
    followUp: followUpSchema.optional(),
});
export const setDncBodySchema = z.object({
    reason: z.string().trim().min(1, 'Bitte geben Sie einen Grund an.').max(1000),
    requestGdprDeletion: z.boolean().optional(),
});
export const liftDncBodySchema = z.object({
    reason: z.string().trim().min(1, 'Bitte geben Sie einen Grund an.').max(1000),
    followUp: followUpSchema,
});
/** Dispositions of the call editor (II.E1) in the order of the chips. */
export const callDispositions = [
    'reached',
    'not_reached',
    'voicemail',
    'callback',
    'documents_promised',
    'no_interest',
    'dnc',
];
export const callDispositionLabels = {
    reached: 'Erreicht · Ergebnis notieren',
    not_reached: 'Nicht erreicht',
    voicemail: 'Mailbox',
    callback: 'Wiedervorlage',
    documents_promised: 'Unterlagen zugesagt',
    no_interest: 'Kein Interesse',
    dnc: 'DNC setzen',
};
export const logCallBodySchema = z.object({
    disposition: z.enum(callDispositions),
    note: z.string().trim().max(4000).optional(),
    durationSeconds: z.number().int().min(0).max(86_400).optional(),
    followUp: z.object({ dueOn: dayKeySchema, dueTime: timeOfDaySchema.optional() }).optional(),
    taskId: z.string().uuid().optional(),
    /** Softphone call of this record (POST /calls, P-45): the disposition is written onto that call row. */
    callId: z.string().uuid().optional(),
});
export const noteBodySchema = z.object({
    text: z.string().trim().min(1, 'Pflichtangabe.').max(10_000),
    followUp: z.object({ dueOn: dayKeySchema, dueTime: timeOfDaySchema.optional() }).optional(),
});
export const updateNoteBodySchema = z.object({
    text: z.string().trim().min(1, 'Pflichtangabe.').max(10_000),
});
export const contactPhoneInputSchema = z.object({
    id: z.string().uuid().optional(),
    number: z.string().trim().min(1, 'Pflichtangabe.').max(40),
    type: z.string().trim().max(40).nullable().optional(),
    isPrimary: z.boolean().optional(),
});
export const contactEmailInputSchema = z.object({
    id: z.string().uuid().optional(),
    email: z.string().trim().min(1, 'Pflichtangabe.').max(200),
    isPrimary: z.boolean().optional(),
});
/** Properties editing (Kontaktakte I.07); owner and referrer changes need a reason. */
export const updateContactBodySchema = z.object({
    firstName: z.string().trim().min(1, 'Pflichtangabe.').max(100).optional(),
    lastName: z.string().trim().min(1, 'Pflichtangabe.').max(100).optional(),
    salutation: z.string().trim().max(40).nullable().optional(),
    street: z.string().trim().max(200).nullable().optional(),
    postalCode: z.string().trim().max(20).nullable().optional(),
    city: z.string().trim().max(120).nullable().optional(),
    bornOn: dayKeySchema.nullable().optional(),
    phones: z.array(contactPhoneInputSchema).max(10).optional(),
    emails: z.array(contactEmailInputSchema).max(10).optional(),
    gdprOptIn: z.boolean().optional(),
    whatsappOptIn: z.boolean().optional(),
    ownerId: z.string().uuid().nullable().optional(),
    referrerId: z.string().uuid().nullable().optional(),
    reason: z.string().trim().max(1000).optional(),
});
export const bulkContactActions = ['change_owner', 'change_lifecycle', 'add_to_list'];
export const bulkContactBodySchema = z.object({
    ids: z.array(z.string().uuid()).min(1, 'Bitte Kontakte auswählen.').max(500),
    action: z.enum(bulkContactActions),
    ownerId: z.string().uuid().nullable().optional(),
    reason: z.string().trim().max(1000).optional(),
    lifecycle: lifecycleChangeBodySchema.optional(),
    callListId: z.string().uuid().optional(),
});
/** 403 message when a setter tries to change a contact's owner (owner decision 12.10.2026). */
export const CONTACT_OWNER_CHANGE_FORBIDDEN_MESSAGE = 'Den Besitzer ändern Berater, Vertriebsleitung, Backoffice oder Admin.';
/**
 * Who may change a contact's owner by hand (record, bulk action): everybody who sees the contact except
 * setters (invariant 6 already forbids setters as owners). The system's own assignment stays untouched.
 */
export function canChangeContactOwner(roleKey) {
    return roleKey !== RoleKey.setter;
}
/** `financing`: the financeable volume of the newest returned Selbstauskunft, highest first (D-381). */
export const contactListSorts = ['name', 'newest', 'oldest', 'next_step', 'financing'];
/** Filters of the list, the counts and the export (Kontakte 01/02). */
export const contactFilterSchema = z.object({
    q: z.string().trim().max(120).optional(),
    lifecycle: z.string().trim().max(60).optional(),
    owner: z.string().trim().max(40).optional(),
    source: z.string().trim().max(100).optional(),
    /** The external referrer partner the contact came in through („Tippgeber-Partner", D-248). */
    referrerPartnerId: z.string().uuid().optional(),
    /**
     * The submitter („Einreicher", owner decision 02.11.2026) within `referrerPartnerId`: his e-mail (case-insensitive);
     * contacts whose first referrer self-disclosure of that partner he submitted. Requires `referrerPartnerId`.
     */
    referrerSubmitter: z.string().trim().toLowerCase().min(1).max(200).optional(),
    /** Entry date („Eingang", FB-003): calendar days in Europe/Berlin, both inclusive. */
    createdFrom: dayKeySchema.optional(),
    createdUntil: dayKeySchema.optional(),
    /** „Finanzierbar" (D-381): contacts whose newest returned Selbstauskunft has a calculation that has not expired. */
    financing: z.enum(['calculated']).optional(),
});
/**
 * Financing capacity of a contact from its newest returned Selbstauskunft (D-380…D-382): `none` (nothing sent),
 * `sent` (link out, not back), `returned` (signed, no calculation), `calculated` (valid), `expired` (older than
 * the validity of `selfDisclosureFormRules.volumeValidityDays`, counted in Berlin calendar days).
 */
export const contactFinancingStates = ['none', 'sent', 'returned', 'calculated', 'expired'];
/** Roles that never see a Selbstauskunft and therefore no financing capacity (setter, referrer, developer). */
export function financingVisibleForRole(roleKey) {
    return roleKey !== RoleKey.setter && roleKey !== RoleKey.referrer && roleKey !== RoleKey.developerReadonly;
}
/** 400 message when the entry date range ends before it starts. */
export const CONTACT_CREATED_RANGE_MESSAGE = 'Das Enddatum liegt vor dem Startdatum.';
/** `referrerSubmitter` without `referrerPartnerId` (400 `fields.referrerSubmitter`). */
export const CONTACT_SUBMITTER_PARTNER_MESSAGE = 'Bitte wählen Sie zuerst einen Tippgeber-Partner.';
export const exportContactsBodySchema = z.object({
    ids: z.array(z.string().uuid()).max(10_000).optional(),
    filter: contactFilterSchema.optional(),
});
export const mergeFieldSides = ['a', 'b'];
export const mergeFieldChoicesSchema = z.object({
    name: z.enum(mergeFieldSides),
    phone: z.enum(mergeFieldSides),
    email: z.enum(mergeFieldSides),
    lifecycle: z.enum(mergeFieldSides),
    owner: z.enum(mergeFieldSides),
});
export const mergeBodySchema = z.object({ fields: mergeFieldChoicesSchema });
export const requestAccessBodySchema = z.object({
    message: z.string().trim().max(1000).optional(),
});
/**
 * Board columns (Kontakte 03): seven columns, the last one combines No Show and Nicht erreicht (A6); „Interessent"
 * stands right before „Kunde" (D-430).
 */
export const contactBoardColumns = [
    'new',
    'contacted',
    'call_booked',
    'follow_up',
    'prospect',
    'customer',
    'no_show_not_reached',
];
export const contactBoardColumnLabels = {
    new: 'Neu',
    contacted: 'Kontaktiert',
    call_booked: 'Call gebucht',
    follow_up: 'Follow Up',
    prospect: 'Interessent',
    customer: 'Kunde',
    no_show_not_reached: 'No Show · n. erreicht',
};
/** Tools of the record's action bar (Kontaktakte I.01–I.11). */
export const contactTools = [
    'call',
    'whatsapp',
    'email',
    'appointment',
    'task',
    'document',
    'selfDisclosure',
    'note',
];
export const contactToolLabels = {
    call: 'Anruf',
    whatsapp: 'WhatsApp',
    email: 'E-Mail',
    appointment: 'Termin',
    task: 'Aufgabe',
    document: 'Dokument',
    selfDisclosure: 'Selbstauskunft',
    note: 'Notiz',
};
/** Filter axes of the timeline (Kontaktakte I.05). */
export const timelineChannels = ['call', 'email', 'whatsapp', 'sms', 'portal'];
export const timelineKinds = ['messages', 'calls', 'tasks', 'appointments', 'system'];
export const timelineOrigins = ['contact', 'deals', 'tickets', 'portal'];
export const timelineChannelLabels = {
    call: 'Anruf',
    email: 'E-Mail',
    whatsapp: 'WhatsApp',
    sms: 'SMS',
    portal: 'Portal',
};
export const timelineKindLabels = {
    messages: 'Nachrichten',
    calls: 'Anrufe',
    tasks: 'Aufgaben',
    appointments: 'Termine',
    system: 'System',
};
export const timelineOriginLabels = {
    contact: 'Kontakt',
    deals: 'Deals',
    tickets: 'Tickets',
    portal: 'Portal',
};
/** Default hint of the properties edit mode (Kontaktakte A3). */
export const contactEditNote = 'Klicken Sie in ein Feld, um es zu ändern. Besitzer, Lifecycle und DNC verlangen einen Grund.';
//# sourceMappingURL=contacts.js.map